import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import { fromJS, Map, List } from 'immutable';
import { isEqual } from 'underscore';
import classNames from 'classnames';

import Popup from '../../helpers/Popup';
import Button from '../../helpers/Button';
import Checkbox from '../../helpers/Checkbox';
import BaseSelect from '../../helpers/Select';
import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import { ProjectMenu } from '../../Project/ProjectMenu';
import { getOriginalIn, getIn } from '../../../utils/ChangeSpy';
import config from '../../../config';
import { ProjectSuggestionInput } from './ProjectSuggestionInput';
import { ProjectTextArea } from './ProjectTextArea';
import { ProjectTextInput } from './ProjectTextInput';
import { OneColumnTable } from './OneColumnTable';

import style from './style.module.css';

const DELETE_MENU_ID = 'eprojectDeleteContextMenu';

const types = config.values.getIn(['eproject', 'types']);
const Select = PrettyError(DisplayError(BaseSelect));

/**
 * Context menu rendered into body: Draggable transforms the dialog, which would offset fixed-positioned menus.
 */
const ProjectMenus = ({ onDelete }) =>
  createPortal(<ProjectMenu id={DELETE_MENU_ID} onDelete={onDelete} />, document.body);

/**
 * Popup for editing executive project details, industries, harvco tags and users.
 *
 * @param props {Object}.
 * @param props.projectData {Immutable.Map} Project information data.
 * @param props.users {Immutable.List} User list.
 * @param props.harvcoTags {Immutable.List} Harvco tag list.
 * @param props.common {Immutable.Map} Common object.
 * @param props.isSaving {Boolean} Saving status.
 * @param props.onSave {Function} Handle event of saving details.
 * @param props.onCancel {Function} Handle event of discarding changes.
 */
const EProjectDetailsPopupComponent = ({
  projectData,
  users,
  harvcoTags,
  common,
  isSaving,
  onSave,
  onCancel,
  onReset,
  onTextChange,
  onFocus,
  onSuggestionClose,
  onSuggestionSelect,
  onSuggestionInputClick,
  onRowClick,
  onRowDoubleClick,
  onRowInsert,
  onDeleteRow,
  onKeyPress,
}) => {
  const projectType = types.get('PROJECT');
  const industriesChanged =
    !isEqual(getIn(common, 'selectedBICs').toJS(), getOriginalIn(common, 'selectedBICs').toJS()) ||
    !isEqual(getIn(common, 'selectedBMs').toJS(), getOriginalIn(common, 'selectedBMs').toJS());
  const projectTypeOptions = common.get('projectTypes').map(name => fromJS({ name, value: name }));

  const footer = (
    <>
      <Button className="btn-primary" disabled={isSaving} onClick={onSave}>
        Save
      </Button>
      <Button onClick={onCancel}>
        Cancel
      </Button>
    </>
  );

  return (
    <Popup
      className={style.host}
      footer={footer}
      header="Edit project details"
      id="eprojectDetailsPopup"
      loading={isSaving}
    >
      <div className={style.content} onClick={onReset}>
        <div className={style.fields}>
          <ProjectTextInput field={projectData.get('category')} onTextChange={onTextChange} />
          <div className="form-group">
            <Select
              className={classNames({ 'dirty-field': projectData.getIn(['projectType', 'dirty']) }, 'input-sm')}
              defaultText=" "
              defaultValue=""
              label="Type"
              labelClassName="field-label"
              name="projectType"
              onChange={event => onTextChange({ event, data: { type: projectType } })}
              options={projectTypeOptions}
              root={projectData}
              value={projectData.getIn(['projectType', 'name'])}
              showLabel
            />
          </div>
          <ProjectSuggestionInput
            id="eprojectHarvCoLead"
            label="HarvCo Lead"
            name="harvcoLead"
            onFocus={onFocus}
            onSuggestionClose={onSuggestionClose}
            onSuggestionInputClick={onSuggestionInputClick}
            onSuggestionSelect={onSuggestionSelect}
            onTextChange={onTextChange}
            row={projectData.get('harvcoLead')}
            suggestions={common.get('harvcoLeadSuggestions')}
          />
          <ProjectSuggestionInput
            id="eprojectClientLead"
            label="Client Lead"
            name="clientLead"
            onFocus={onFocus}
            onSuggestionClose={onSuggestionClose}
            onSuggestionInputClick={onSuggestionInputClick}
            onSuggestionSelect={onSuggestionSelect}
            onTextChange={onTextChange}
            row={projectData.get('clientLead')}
            suggestions={common.get('clientLeadSuggestions')}
          />
          <div className="form-group">
            <label className="field-label" htmlFor="eprojectClientInput">
              Client
            </label>
            <input
              className="form-control"
              id="eprojectClientInput"
              value={projectData.get('abbrName') || ''}
              disabled
            />
          </div>
        </div>
        <div
          className={classNames('group-checkbox', style.active, {
            changed: projectData.getIn(['status', 'dirty']),
          })}
        >
          <Checkbox
            checked={projectData.get('status')}
            label="Active"
            name="status"
            onChange={event => onTextChange({ event, data: { type: projectType } })}
            onClick={event => event.stopPropagation()}
          />
        </div>
        <ProjectTextArea
          field={projectData.get('details')}
          id="eprojectDetails"
          label="Project Scope"
          name="details"
          onTextChange={onTextChange}
        />
        <div className={style.tables}>
          <OneColumnTable
            changed={industriesChanged}
            contextId={DELETE_MENU_ID}
            onRowDoubleClick={onRowDoubleClick}
            onRowInsert={onRowInsert}
            rows={common.get('bic').concat(common.get('bm'))}
            title="Industries"
            type={types.get('INDUSTRIES')}
            skipMenu
          />
          <OneColumnTable
            contextId={DELETE_MENU_ID}
            onKeyPress={onKeyPress}
            onRowClick={onRowClick}
            onRowInsert={onRowInsert}
            onTextChange={onTextChange}
            rows={harvcoTags}
            title="Harvco Tags"
            type={types.get('HARVCO_TAGS')}
          />
          <OneColumnTable
            contextId={DELETE_MENU_ID}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            onRowInsert={onRowInsert}
            onSuggestionClose={onSuggestionClose}
            onSuggestionSelect={onSuggestionSelect}
            onTextChange={onTextChange}
            rows={users}
            suggestions={common.get('userSuggestions')}
            title="Users"
            type={types.get('USERS')}
          />
        </div>
      </div>
      <ProjectMenus onDelete={onDeleteRow} />
    </Popup>
  );
};

EProjectDetailsPopupComponent.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  harvcoTags: PropTypes.instanceOf(List).isRequired,
  isSaving: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onRowDoubleClick: PropTypes.func.isRequired,
  onRowInsert: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSuggestionClose: PropTypes.func.isRequired,
  onSuggestionInputClick: PropTypes.func.isRequired,
  onSuggestionSelect: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
  projectData: PropTypes.instanceOf(Map).isRequired,
  users: PropTypes.instanceOf(List).isRequired,
};

export const EProjectDetailsPopup = memo(EProjectDetailsPopupComponent);
